import { Box, Text, Flex, useDisclosure } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import SearchInput from "@root/components/common/SearchInput";
import { DataTable } from "@root/components";
import { TableColumns } from "@root/components/kpec-admin/subscriber-management/TableColumns";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RegisterSubscriberButton, ExcelUploadButton } from "./style";
import { AddSubscriberModal } from "@root/components/kpec-admin/subscriber-management/addsubscriberModal";
import { ExcelUploadModal } from "@root/components/kpec-admin/subscriber-management/ExcelUploadModal";
import { PlusIcon } from "@root/assets/admin/icons/plusicon";
import { ModalDelete } from "@root/components/modal-delete";
import SubscriberDrawer from "./subscriber-drawer";
import { useSubscriberContext } from "@root/context/use-subscriber-context";
import CustomSelect from "@root/components/common/CustomSelect";
import {
  DELETE,
  EDIT,
  NewsPaperType,
  PAGINATION_DEFAULTS,
} from "@root/constants";
import { ProfileChangeHistoryDrawer } from "@root/components/common/drawer/ProfileChangeHistoryDrawer";
import { useGlobalContext } from "@root/context/global-context";
import { useOrganizationBranding } from "@root/context/organization-branding-context";

const SubscriberManagement = () => {
  const {
    fetchSubscribers,
    subscribers,
    deleteSubscriber,
    importSubscribers,
    pageNo,
    setPageNo,
    pageCount,
  } = useSubscriberContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [editingSubscriber, setEditingSubscriber] = useState<any>(null);
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isExcelOpen,
    onOpen: onExcelOpen,
    onClose: onExcelClose,
  } = useDisclosure();
  const PAGE_SIZE = 10;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedCourse, setSelectedSubscriber] = useState<any>(null);
  const [newspaperType, setNewspaperType] = useState<string>("");
  const [adminType, setAdminType] = useState<string>("");
  const { getChatHistory, userHistory, historyPage } = useGlobalContext();
  const [isViewHistoryOpen, setIsViewHistoryOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const { slug } = useOrganizationBranding();

  const handleRowClick = (course: any) => {
    setSelectedSubscriber({
      title: course?.title,
      completion: course?.completion ?? 0,
    });
    setDrawerOpen(true);
  };

  const handleChangeHistoryClick = async (id: any) => {
    setSelectedUserId(id); // Store the ID
    const payload = {
      id: id,
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      offset: (historyPage - 1) * PAGINATION_DEFAULTS.PAGESIZE,
    };
    try {
      await getChatHistory(payload);
      setIsViewHistoryOpen(true);
    } catch (error) {
      // Error fetching user history
    }
  };

  function handleActions(type: string, rowData: any) {
    if (type === EDIT) {
      setEditingSubscriber(rowData);
      onEditOpen();
    } else if (type === DELETE) {
      setIsDeleteModal(true);
      setDeleteId(rowData.id);
    }
  }
  function handlePageChange(newPage: number) {
    setPageNo(newPage);
    fetchSubscribers({
      offset: (newPage - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      search: searchTerm || "",
      ...(newspaperType && { newsPaperType: newspaperType }),
      ...(adminType && { role: adminType }),
    });
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const removeSusbcriber = () => {
    deleteSubscriber(deleteId).finally(() => {
      setIsDeleteModal(false);
      setDeleteId("");
    });
  };
  const newspaperOptions = [
    {
      label: t("subscriberManagement.daily"),
      value: String(NewsPaperType.Daily),
    },
    {
      label: t("subscriberManagement.weekly"),
      value: String(NewsPaperType.Weekly),
    },
    {
      label: t("subscriberManagement.other"),
      value: String(NewsPaperType.Other),
    },
  ];

  const handleNewspaperTypeChange = (value: string) => {
    setNewspaperType(value);
  };

  const handleAdminTypeChange = (value: string) => {
    setAdminType(value);
  };

  const adminTypeOptions = [
    { label: t("signup.subscriber", "Subscriber"), value: "3" },
    { label: t("signup.nonSubscriber", "Non-Subscriber"), value: "4" },
  ];

  // Fires when filters change — always resets to page 1.
  // pageNo is intentionally excluded from deps: page changes go through handlePageChange directly.
  useEffect(() => {
    if (!slug) return;
    setPageNo(1);
    fetchSubscribers({
      offset: 0,
      limit: PAGE_SIZE,
      search: searchTerm || "",
      ...(newspaperType && { newsPaperType: newspaperType }),
      ...(adminType && { role: adminType }),
    });
  }, [slug, searchTerm, newspaperType, adminType]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshSubscribers = () => {
    const payload = {
      offset: (pageNo - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
      slug: slug ?? "",
      search: searchTerm || "",
      ...(newspaperType && { newsPaperType: newspaperType }),
      ...(adminType && { role: adminType }),
    };
    fetchSubscribers(payload);
  };

  return (
    <Flex direction="column" height="100%" bg="white" padding={"24px 16px"}>
      <Box display={"flex"} justifyContent={"space-between"} mt="4">
        <Box>
          <Text
            fontWeight={600}
            color={"#2F2F34"}
            fontSize={"24px"}
            mb={"10px"}
          >
            {t("subscriberManagement.title")}
          </Text>
          <Text color={"#798692"} maxWidth={"600px"}>
            {t("subscriberManagement.description")}
          </Text>
        </Box>
        <Flex align="center" gap="0">
          <ExcelUploadButton onClick={onExcelOpen}>
            {t("subscriberManagement.excelUpload", "Excel Upload")}
          </ExcelUploadButton>
          <RegisterSubscriberButton onClick={onOpen}>
            <PlusIcon boxSize={5} color="#FFFFFF" />
            {t("subscriberManagement.registerNewSubscriber")}
          </RegisterSubscriberButton>
        </Flex>
        <ExcelUploadModal
          isOpen={isExcelOpen}
          onClose={onExcelClose}
          onImport={async (file) => {
            await importSubscribers(file);
            onExcelClose();
            refreshSubscribers();
          }}
        />
        <AddSubscriberModal
          isOpen={isOpen}
          onClose={onClose}
          onSuccess={refreshSubscribers}
        />
        <AddSubscriberModal
          isOpen={isEditOpen}
          onClose={() => {
            setEditingSubscriber(null);
            onEditClose();
          }}
          onSuccess={refreshSubscribers}
          subscriber={editingSubscriber}
        />
      </Box>

      <Flex justifyContent={"space-between"} my={10}>
        <SearchInput
          placeholder={t("subscriberManagement.searchPlaceholder")}
          width="400px"
          value={searchTerm}
          onChange={handleSearchChange}
        />

        <Flex gap={3} align="center">
          <CustomSelect
            borderRadius={"12px"}
            height={"44px"}
            mt={9}
            placement="bottom-end"
            value={adminType}
            onChange={(value: string) => handleAdminTypeChange(value)}
            placeholder={t('subscriberManagement.adminType')}
            options={adminTypeOptions}
          />
          <CustomSelect
            borderRadius={"12px"}
            height={"44px"}
            mt={9}
            placement="bottom-end"
            value={newspaperType}
            onChange={(value: string) => handleNewspaperTypeChange(value)}
            placeholder={t("subscriberManagement.subscriberType")}
            options={newspaperOptions}
          />
        </Flex>
      </Flex>
      <ProfileChangeHistoryDrawer
        isOpen={isViewHistoryOpen}
        onClose={() => {
          setIsViewHistoryOpen(false);
          setSelectedUserId(null); // Clear the ID when closing
        }}
        viewHistoryChange={handleChangeHistoryClick}
        userId={selectedUserId} // Pass the ID to the drawer
      />

      <DataTable
        columns={TableColumns(
          handleActions,
          navigate,
          handleChangeHistoryClick,
        )}
        data={subscribers}
        showPagination={true}
        page={pageNo}
        pageCount={pageCount}
        pageSize={10}
        handlePageChange={handlePageChange}
        theadBg="#dad5f5"
        rowHoverBg="gray.50"
        defaultSortState={[{ id: "fullName", desc: false }]}
      />

      <ModalDelete
        isOpen={isDeleteModal}
        onClose={() => {
          setIsDeleteModal(false);
        }}
        onConfirm={() => removeSusbcriber()}
        title={t("subscriberManagement.removeSubscriber")}
        description={t("subscriberManagement.removeDescription")}
        chatCountDescription={t("subscriberManagement.removeConfirm")}
        deleteButtonTitle={t("subscriberManagement.delete")}
        cancelButtonTitle={t("subscriberManagement.cancel")}
      />

      <SubscriberDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        course={selectedCourse}
      />
    </Flex>
  );
};

export default SubscriberManagement;
